import { OffsetRange } from '../core/offsetRange.js';

/**
 * A single atomic replacement: replace `range` in the input with `newText`.
 */
export class StringReplacement {
	constructor(
		readonly range: OffsetRange,
		readonly newText: string,
	) { }

	get isEmpty(): boolean {
		return this.range.isEmpty && this.newText.length === 0;
	}

	toString(): string {
		return `${this.range}->${JSON.stringify(this.newText)}`;
	}
}

/**
 * A set of replacements applied in parallel to one input string.
 *
 * Invariants:
 *  - replacements are sorted by `range.start`
 *  - replacements do not overlap (touching is allowed)
 *  - all ranges live in the same coordinate space (the input passed to `apply`)
 */
export class StringEdit {
	static readonly empty: StringEdit = new StringEdit([]);

	static single(replacement: StringReplacement): StringEdit {
		return new StringEdit([replacement]);
	}

	/**
	 * Validates that replacements are sorted and non-overlapping.
	 */
	static of(replacements: readonly StringReplacement[]): StringEdit {
		for (let i = 1; i < replacements.length; i++) {
			const prev = replacements[i - 1];
			const cur = replacements[i];
			if (prev.range.endExclusive > cur.range.start) {
				throw new Error(
					`StringEdit.of: replacements must be sorted and non-overlapping (` +
					`${prev.range} vs ${cur.range})`
				);
			}
		}
		return new StringEdit(replacements);
	}

	private constructor(
		readonly replacements: readonly StringReplacement[],
	) { }

	get isEmpty(): boolean {
		return this.replacements.length === 0;
	}

	apply(text: string): string {
		if (this.replacements.length === 0) { return text; }
		const parts: string[] = [];
		let cursor = 0;
		for (const r of this.replacements) {
			parts.push(text.substring(cursor, r.range.start));
			parts.push(r.newText);
			cursor = r.range.endExclusive;
		}
		parts.push(text.substring(cursor));
		return parts.join('');
	}

	/**
	 * Returns an edit `E` such that `E.apply(this.apply(input)) === input`.
	 */
	inverse(input: string): StringEdit {
		const out: StringReplacement[] = [];
		let delta = 0;
		for (const r of this.replacements) {
			const newStart = r.range.start + delta;
			const newEnd = newStart + r.newText.length;
			const oldText = input.substring(r.range.start, r.range.endExclusive);
			out.push(new StringReplacement(new OffsetRange(newStart, newEnd), oldText));
			delta += r.newText.length - r.range.length;
		}
		return new StringEdit(out);
	}

	/**
	 * Joins touching replacements and drops no-ops.
	 */
	normalize(): StringEdit {
		const out: StringReplacement[] = [];
		for (const r of this.replacements) {
			if (r.isEmpty) { continue; }
			const prev = out[out.length - 1];
			if (prev && prev.range.endExclusive === r.range.start) {
				out[out.length - 1] = new StringReplacement(
					new OffsetRange(prev.range.start, r.range.endExclusive),
					prev.newText + r.newText,
				);
			} else {
				out.push(r);
			}
		}
		return new StringEdit(out);
	}

	toString(): string {
		return `StringEdit[${this.replacements.map(r => r.toString()).join(', ')}]`;
	}
}
